import { yupResolver } from "@hookform/resolvers/yup";
import { Button, useDisclosure } from "@nextui-org/react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import * as yup from 'yup';
import { getFetcher, postFetcher, putFetcher, putFetcherWithoutId } from "../../services/api";
import useSWR from "swr";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import useListCompany from "../useListCompany";
import useBadRequestForm from "../useBadRequestForm";
import { vehicleValidation } from "../../services/validation/vehicle.validation";
import useAuth from "../useAuth";
import RolesAccess from "../../enums/roles";
import { hasValidRole } from "../../services/helpers";

export default function useVehicle() {
    const {data, isLoading, mutate} = useSWR('/api/vehicles/search?returnAll=true', getFetcher)
    const { isResponseErrorObject, formErrorMessage, setFormErrorMessage } = useBadRequestForm()
    const { dataCompany, isLoadingCompany } = useListCompany()
    const {isOpen: isOpenModalForm, onOpen: onOpenModalForm, onOpenChange: onOpenChangeModalForm, onClose: onCloseModalForm} = useDisclosure();
    const {isOpen: isOpenModalAlert, onOpenChange: onOpenChangeModalAlert} = useDisclosure();
    const [editId, setEditId] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [kendaraanDihapus, setKendaraanDihapus] = useState(null);
    const { roles } = useAuth();

    // Penghapusan kendaraan menuntut alasan dan surat keterangan, dan hanya
    // Super Admin yang boleh melakukannya - tombolnya pun tidak ditampilkan
    // untuk peran lain, sejalan dengan penjagaan di sisi server.
    const bolehMenghapus = hasValidRole(roles, [RolesAccess.SUPER_ADMIN]);
    const { handleSubmit, reset, control, formState: { isSubmitting } } = useForm({resolver: yupResolver(vehicleValidation)});

    const columns = useMemo(() =>  [
        {field: 'noPolisi',headerName: 'No. Polisi'},
        {field: 'modelKendaraan',headerName: 'Merk Kendaraan'},
        {field: 'tahunPembuatan',headerName: 'No. mesin'},
        {field: 'nomorRangka',headerName: 'No. Rangka'},
        {field: 'nomorMesin',headerName: 'No. mesin'},
        {field: 'kepemilikan',headerName: 'Kepemilikan', valueGetter: (value, row) => `${row.company.name} - ${row.kepemilikan}`},
        {
            field: 'action',
            headerName: 'Aksi',
            // Tombol aksi mengikuti nada tema: ikon berwarna di atas latar
            // transparan, bukan kotak warna penuh, supaya isi tabel tetap jadi
            // fokus. Ukuran sm dan ikon size-4 sama seperti tabel lain.
            renderCell: (params) => (
                <div className="flex items-center gap-1">
                    <Button size='sm' variant='light' isIconOnly aria-label='Ubah kendaraan' title='Ubah kendaraan' className='text-ink-700' onPress={() => onClickEdit(params.row)}><PencilSquareIcon className='size-4'/></Button>
                    {bolehMenghapus && (
                        <Button size='sm' variant='light' color='danger' isIconOnly aria-label='Hapus kendaraan' title='Hapus kendaraan' onPress={() => onClickDelete(params.row)}><TrashIcon className='size-4'/></Button>
                    )}
                </div>
            ),
            sortable: false,
            filterable: false
        },
    ], [onClickEdit, onClickDelete, bolehMenghapus]);

    function onClickEdit(item) {    
        setEditId(item.id);
        setIsEdit(true);
        reset({
            companyId: item.companyId,
            noPolisi: item.noPolisi,
            modelKendaraan: item.modelKendaraan,
            tahunPembuatan: item.tahunPembuatan,
            nomorRangka: item.nomorRangka,
            nomorMesin: item.nomorMesin,
            kepemilikan: item.kepemilikan
        });           
        onOpenChangeModalForm();
    }

    function onCloseForm() {
        setEditId(null);
        setIsEdit(false);
        reset({
            
        });
        onCloseModalForm();
        setFormErrorMessage(null);
    }

    /*
     * Membawa SELURUH baris, bukan cuma id: formulir penghapusan menampilkan
     * ulang perusahaan, nomor polisi, nomor mesin, dan nomor rangka supaya yang
     * akan dihapus benar-benar terlihat sebelum ditekan.
     */
    function onClickDelete(row) {
        setKendaraanDihapus(row);
        onOpenChangeModalAlert();
    }

    function onTutupModalHapus() {
        setKendaraanDihapus(null);
        onOpenChangeModalAlert();
    }

    function onBerhasilHapus() {
        setKendaraanDihapus(null);
        mutate();
    }

    async function onSubmitForm(data) {
        try {
            if (isEdit) {
                data.vehicleId = editId
                delete data.companyId
                await putFetcherWithoutId('/api/vehicles', data);
                mutate()
                toast.success('Kendaraan berhasil diubah!');
            } else {
                await postFetcher('/api/vehicles/add-to-company', data);
                mutate()
                toast.success('Kendaraan berhasil ditambah!');
            }
            onCloseForm();
        } catch (error) {
            isResponseErrorObject(error.response.data)
        }
    }

    return {
        dataFetch: {
            data,
            isLoading,
            dataCompany,
            isLoadingCompany
        },
        modalForm: {
            isOpenModalForm,
            onOpenModalForm,
            onOpenChangeModalForm,
        },
        modalHapus: {
            isOpenModalHapus: isOpenModalAlert,
            onOpenChangeModalHapus: onOpenChangeModalAlert,
            onTutupModalHapus,
            kendaraanDihapus,
            onBerhasilHapus,
        },
        hookForm: { 
            handleSubmit, 
            reset, 
            control,
            formState: { isSubmitting }
        },
        badRequest: {
            formErrorMessage
        },
        isEdit,
        formErrorMessage,
        columns,
        onClickEdit,
        onCloseForm,
        onClickDelete,
        onSubmitForm,
    }
}